import { CircularLoader } from '@/components/CircularLoader';
import { TFunction } from 'i18next';
import React from 'react';
import { RequestStatus } from '@/types/request';
import { Error as ErrorIcon } from '@styled-icons/material-outlined/Error';
import { CheckCircle } from '@styled-icons/material/CheckCircle';
import Infobox from '@/components/Modal/Infobox';
import Input from '@/components/Input';
import format from 'date-fns/format';
import { ErrorMessage } from '@/components/ErrorMessage';
import Modal from '@/components/Modal';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  ListItem,
  OrderedList,
  Select,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MapData, MapGroup } from '../../types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { mapNameRule } from '@/validations';
export interface MergingResult {
  mergedMap: (string | undefined)[];
  failedMap: (string | undefined)[];
}
interface MergeMapProps {
  t: TFunction;
  onClose: () => void;
  onConfirm: (mapName: string, groupKey: string) => Promise<RequestStatus<MergingResult>>;
  selectedMaps: MapData[];
  show: boolean;
  mapGroups: MapGroup[];
}

interface MapMergingFormValues {
  newMapName: string;
  mapGroup: string;
}

const MergeMap: React.FC<MergeMapProps> = ({
  t,
  onClose,
  onConfirm,
  show,
  selectedMaps,
  mapGroups,
}) => {
  const getDefaultName = () => format(new Date(), 'yyyy-LLL-do-kk:mm:ss');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [mergeResult, setMergeResult] = React.useState<MergingResult>({
    mergedMap: ['awew'],
    failedMap: ['wqeqweq', 'sdfdsr'],
  });

  const schema = yup.object().shape({
    newMapName: mapNameRule.label(t('map_name')),
    mapGroup: yup.string().required().label(t('map_gtoup')),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MapMergingFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { newMapName: getDefaultName() },
  });

  React.useEffect(() => {
    reset();
    setMergeResult({
      mergedMap: [],
      failedMap: [],
    });
    setError('');
  }, [show]);

  const handleConfirm = async (values: MapMergingFormValues) => {
    setLoading(true);
    setError('');
    const { success, message, data } = await onConfirm(values.newMapName, values.mapGroup);
    if (data) {
      const { failedMap, mergedMap } = data;
      setMergeResult({ failedMap, mergedMap });
      setLoading(false);
      return;
    }
    if (!success && message) {
      setError(message);
      setLoading(false);
      return;
    }
    setLoading(false);
    onClose();
  };

  if (mergeResult.failedMap.length || mergeResult.mergedMap.length) {
    return (
      <Modal
        footer={
          <HStack>
            <Button colorScheme="blue" variant="solid" onClick={onClose}>
              {t('close')}
            </Button>
          </HStack>
        }
        show={show}
        title={t('fail_to_merge_some_map')}
        onClose={onClose}>
        <Stack>
          <OrderedList>
            {mergeResult.mergedMap.map((m) => (
              <ListItem key={m} color="green.500">
                {m}
              </ListItem>
            ))}
          </OrderedList>
          <OrderedList>
            {mergeResult.failedMap.map((m) => (
              <ListItem key={m} color="red.500">
                {m}
              </ListItem>
            ))}
          </OrderedList>
          <Text fontSize="sm">{t('map_merging_fail_description')}</Text>
        </Stack>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        allowClose={false}
        footer={
          <HStack>
            <Button colorScheme="blue" size="sm" variant="solid" w="100px" onClick={onClose}>
              {t('close')}
            </Button>
          </HStack>
        }
        show={show}
        title={t('map_merging_fail')}
        onClose={onClose}>
        <Stack>
          <Text fontSize="sm">{t('map_merging_fail_description')}</Text>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      footer={
        <HStack>
          <Button colorScheme="gray" size="sm" variant="ghost" w="100px" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            colorScheme="blue"
            size="sm"
            variant="solid"
            type="submit"
            form="mapMergingForm"
            isLoading={loading}
            loadingText={t('processing')}>
            {t('merge')}
          </Button>
        </HStack>
      }
      show={show}
      title={t('merge_maps')}
      onClose={onClose}>
      <Stack>
        <Text fontSize="sm">{t('map_merging_modal_detail')}</Text>
        <Box p="4" maxH="400px" maxW="100%" overflow="scroll">
          <OrderedList>
            {selectedMaps.map((m) => (
              <ListItem key={m.map_name} fontSize="sm">
                {m.map_name}
              </ListItem>
            ))}
          </OrderedList>
        </Box>
        <form onSubmit={handleSubmit(handleConfirm)} id="mapMergingForm">
          <Input
            register={register('newMapName')}
            label={t('map_name')}
            type="text"
            autoFocus={true}
            error={errors.newMapName?.message}
          />
          <FormControl>
            <FormLabel>{t('map_group')}</FormLabel>
            <Select {...register('mapGroup')}>
              {mapGroups?.map((group) => (
                <option key={group.group_key} value={group.group_key}>
                  {group.group_name}
                </option>
              ))}
              <FormHelperText>The new map will belong to the selected map group</FormHelperText>
            </Select>
          </FormControl>
        </form>
      </Stack>
    </Modal>
  );
};

export default MergeMap;
